import React, { FC, useContext } from "react";
import { FormikContext } from "formik";
import ReactSelect, {
  Props as SelectInputProps,
  components,
  MenuListProps,
  OptionProps,
  MultiValueGenericProps,
  SingleValueProps,
} from "react-select";
import classnames from "classnames";
import { Option } from "@shared/interfaces";
import { CheckboxInput } from "@shared/components";

import "./index.scss";

interface SelectInputExtraProps {
  menuOption?: (props: OptionProps) => JSX.Element;
  menuWrapper?: (props: MenuListProps) => JSX.Element;
  multiValueContainer?: (props: MultiValueGenericProps) => JSX.Element;
  customPosition?: boolean;
  onlyIcon?: boolean;
  dropdownIcon?: JSX.Element;
  withCheckbox?: boolean;
  selectAllPlaceholder?: string;
}

const MenuListDefault = (props: MenuListProps) => {
  return <components.MenuList {...props}>{props.children}</components.MenuList>;
};

const MenuOptionDefault = (props: OptionProps) => {
  return <components.Option {...props}>{props.children}</components.Option>;
};

const MenuOptionWithCheckbox = (props: OptionProps) => {
  const { isSelected, label } = props;

  return (
    <components.Option {...props}>
      <div className="option-with-checkbox">
        <CheckboxInput value={Number(isSelected)} ignoreStopPropagation />

        <span>{label}</span>
      </div>
    </components.Option>
  );
};

const MenuListWithSelectAll = (props: MenuListProps) => {
  const { children, getValue, setValue, options } = props;

  const selectedValues = (getValue() ?? []) as Option[];

  const allSelected = options.every((opt) => selectedValues.some((v) => v.value === (opt as Option).value));

  const toggleSelectAll = () => {
    const newValue = allSelected ? [] : options;

    setValue(newValue, "select-option");
  };

  const typedSelectProps = props.selectProps as typeof props.selectProps & {
    selectAllPlaceholder?: string;
  };

  return (
    <components.MenuList {...props}>
      {children}

      <div className="custom-select-divider" style={{ borderTop: "1px solid #eee", margin: "4px 0" }} />

      <div
        className="option-with-checkbox select-all-option"
        style={{ padding: "8px 12px", cursor: "pointer" }}
        onClick={toggleSelectAll}
      >
        <CheckboxInput value={Number(allSelected)} onChange={toggleSelectAll} />

        <span>{typedSelectProps.selectAllPlaceholder || "Select All"}</span>
      </div>
    </components.MenuList>
  );
};

const CustomSingleValue = (props: SingleValueProps) => {
  const selected = (props.getValue() ?? []) as Option[];
  const options = props.selectProps.options as Option[];

  const count = selected.length;
  const total = options.length;

  const typedSelectProps = props.selectProps as typeof props.selectProps & {
    selectAllPlaceholder?: string;
  };

  const label =
    count === 0
      ? typedSelectProps.placeholder
      : count === total
      ? typedSelectProps.selectAllPlaceholder || "All selected"
      : selected.map((v) => v.label).join(", ");

  return <components.SingleValue {...props}>{label}</components.SingleValue>;
};

const SelectInput: FC<SelectInputProps & SelectInputExtraProps> = (props) => {
  const formikContext = useContext(FormikContext);

  const {
    menuWrapper = MenuListDefault,
    menuOption = MenuOptionDefault,
    multiValueContainer = () => null,
    className,
    customPosition,
    dropdownIcon,
    withCheckbox,
    ...inputProps
  } = props;

  return (
    <ReactSelect
      className={classnames(
        "custom-select custom-input",
        { disabled: props.isDisabled, onlyIcon: props.onlyIcon },
        className,
      )}
      {...inputProps}
      menuPortalTarget={customPosition ? document.body : undefined}
      menuPosition={customPosition ? "fixed" : undefined}
      onChange={(newValue, actionMeta) => {
        if (withCheckbox) {
          const selected = newValue as Option;

          let updatedValues: Option[];

          if (Array.isArray(selected)) {
            updatedValues = selected;
          } else {
            updatedValues = Array.isArray(props.value) ? [...props.value] : [];

            const exists = updatedValues.some((v) => v.value === selected.value);

            if (exists) {
              updatedValues = updatedValues.filter((v) => v.value !== selected.value);
            } else {
              updatedValues.push(selected);
            }
          }

          props.onChange?.(updatedValues, actionMeta);

          if (formikContext && props.name) {
            formikContext.setFieldValue(props.name, updatedValues);
          }

          return;
        }

        const valueToChange = props.isMulti ? newValue : newValue ? (newValue as Option).value : null;
        props.onChange && props.onChange(valueToChange, actionMeta);

        if (formikContext && props.name) {
          formikContext.setFieldValue(props.name, valueToChange);
        }
      }}
      components={{
        Option: withCheckbox ? MenuOptionWithCheckbox : menuOption,
        MenuList: withCheckbox ? MenuListWithSelectAll : menuWrapper,
        SingleValue: withCheckbox ? CustomSingleValue : components.SingleValue,
        IndicatorSeparator: () => null,
        MultiValueContainer: multiValueContainer,
        DropdownIndicator: dropdownIcon ? () => dropdownIcon : undefined,
      }}
      classNamePrefix="custom-select"
    />
  );
};

export default SelectInput;
